import React, { useState, useRef, useEffect } from "react";
import { FiUser, FiFileText, FiNavigation, FiBriefcase, FiCreditCard, FiDownload, FiMoreVertical, FiCalendar, FiTag, FiDollarSign, FiPackage } from "react-icons/fi";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { saveAs } from "file-saver";

export default function FinanceTransactionTable({ transactions, refresh }) {
  const [expandedId, setExpandedId] = useState(null);
  const [isExporting, setIsExporting] = useState(false);
  const dropdownRef = useRef(null);

  const formatPeriod = (period) => {
    if (!period) return "N/A";
    if (typeof period === 'string') return period;
    if (period.month && period.year) {
      const date = new Date(period.year, period.month - 1);
      return date.toLocaleString('default', { month: 'long', year: 'numeric' });
    }
    return "N/A";
  };

  const getIcon = (category) => {
    switch (category) {
      case "Donation": return <FiCreditCard className="text-green-500" />;
      case "Payroll": return <FiUser className="text-blue-500" />;
      case "Procurement": return <FiBriefcase className="text-indigo-500" />;
      case "Travel": return <FiNavigation className="text-purple-500" />;
      case "Asset Purchase": return <FiFileText className="text-gray-500" />;
      case "Item Consumption": return <FiPackage className="text-orange-500" />; // ADDED
      default: return <FiFileText className="text-gray-400" />;
    }
  };

  const formatCurrency = (amount, type) => {
    const colorClass = type === "INCOME" ? "text-green-600" : "text-red-600";
    const sign = type === "INCOME" ? "+" : "-";
    return (
      <span className={`font-mono font-medium ${colorClass}`}>
        {sign}${Number(amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
      </span>
    );
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString();
  };

  const getProjectName = (project) => {
    if (!project) return "General";
    if (Array.isArray(project)) {
      if (project.length > 0 && project[0]) {
        return project[0].projectTitle || "Unknown Project";
      }
      return "Unlinked / General";
    }
    if (typeof project === 'object' && project !== null) {
      return project.projectTitle || "Unknown Project";
    }
    return project;
  };

  const handleExport = async (format) => {
    setIsExporting(true);
    try {
      const exportData = transactions.map((t) => {
        return {
          Date: formatDate(t.date),
          Type: t.type,
          Category: t.category,
          Description: t.description,
          Project: getProjectName(t.project),
          Actor: t.actor ? `${t.actor.firstName || ''} ${t.actor.lastName || ''}` : "System",
          Amount: t.amount,
          Status: t.status,
          Reference: t.reference,
          Details: JSON.stringify(t.details) || "-"
        };
      });

      if (exportData.length === 0) {
        alert("No transactions to export!");
        return;
      }

      if (format === "excel") {
        const worksheet = XLSX.utils.json_to_sheet(exportData);
        worksheet["!cols"] = Object.keys(exportData[0]).map((key) => ({ wch: Math.max(key.length, 20) }));
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Transactions");
        XLSX.writeFile(workbook, "Transactions.xlsx");
      } else if (format === "csv") {
        const worksheet = XLSX.utils.json_to_sheet(exportData);
        const csvOutput = XLSX.utils.sheet_to_csv(worksheet);
        saveAs(new Blob([csvOutput], { type: "text/csv;charset=utf-8;" }), "Transactions.csv");
      } else if (format === "pdf") {
        const doc = new jsPDF({ orientation: "landscape" });
        autoTable(doc, {
          head: [Object.keys(exportData[0])],
          body: exportData.map((row) => Object.values(row)),
          headStyles: { fillColor: [237, 125, 49] },
          styles: { fontSize: 8 },
          didDrawPage: (data) => { doc.text("Transactions Export", 20, 15); },
        });
        doc.save("Transactions.pdf");
      }
    } catch (error) {
      alert("Export failed: " + (error.message || "Unknown error"));
    } finally {
      setIsExporting(false);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setExpandedId(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const displayedTransactions = transactions.slice(0, 50);

  return (
    <div className="overflow-x-auto">
      <div className="flex justify-end mb-2">
        <button
          onClick={() => handleExport("excel")}
          disabled={isExporting}
          className="flex items-center gap-2 px-4 py-2 bg-orange-600 text-white rounded hover:bg-orange-700 shadow-sm transition-colors"
        >
          <FiDownload /> {isExporting ? "Exporting..." : "Export Excel"}
        </button>
      </div>

      <table className="min-w-full text-sm text-left bg-white shadow-sm rounded-lg overflow-hidden">
        <thead className="bg-gray-50 text-gray-600 uppercase font-bold text-xs border-b border-gray-200">
          <tr>
            <th className="px-6 py-4">Date</th>
            <th className="px-6 py-4">Type</th>
            <th className="px-6 py-4">Description</th>
            <th className="px-6 py-4">Project</th>
            <th className="px-6 py-4">Actor</th>
            <th className="px-6 py-4 text-right">Amount</th>
            <th className="px-6 py-4 text-center">Status</th>
            <th className="px-6 py-4 text-center">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {displayedTransactions.length === 0 ? (
            <tr>
              <td colSpan="8" className="px-6 py-8 text-center text-gray-500">
                No transactions found matching your filters.
              </td>
            </tr>
          ) : (
            displayedTransactions.map((t) => (
              <React.Fragment key={t._id}>
                <tr className="hover:bg-gray-50 transition-colors group border-b border-gray-50 last:border-0">
                  <td className="px-6 py-4 whitespace-nowrap text-gray-600 font-medium">{formatDate(t.date)}</td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-gray-50 rounded-lg text-gray-600">{getIcon(t.category)}</div>
                      <div>
                        <div className="font-bold text-gray-800 capitalize text-xs">{t.type}</div>
                        <div className="text-xs text-gray-500">{t.category}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 font-medium text-gray-700 max-w-xs truncate">{t.description}</td>
                  
                  <td className="px-6 py-4 text-gray-600">
                    {(() => {
                        const pName = getProjectName(t.project);
                        return pName === "General" || pName === "Unlinked / General" ? (
                          <span className="text-gray-400 italic text-xs">{pName}</span>
                        ) : (
                          <span className="bg-blue-50 text-blue-700 px-3 py-1 rounded-full text-xs font-medium border border-blue-100">
                            {pName}
                          </span>
                        );
                    })()}
                  </td>
                  
                  <td className="px-6 py-4 text-gray-600 text-xs font-medium">
                    {t.actor ? `${t.actor.firstName || ''} ${t.actor.lastName || ''}` : "System"}
                  </td>
                  <td className="px-6 py-4 text-right font-mono">{formatCurrency(t.amount, t.type)}</td>
                  <td className="px-6 py-4 text-center">
                    <span className={`px-3 py-1 rounded-full text-xs font-bold border
                      ${t.status === 'Paid' || t.status === 'Received' ? 'bg-green-50 text-green-700 border-green-200' :
                        t.status === 'Pending' || t.status === 'unpaid' ? 'bg-orange-50 text-orange-700 border-orange-200' :
                        'bg-gray-100 text-gray-600 border-gray-200'}`}>{t.status}</span>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <button
                      onClick={() => setExpandedId(expandedId === t._id ? null : t._id)}
                      className="text-gray-400 hover:text-orange-600 transition-colors p-2 hover:bg-orange-50 rounded-full"
                    >
                      <FiMoreVertical />
                    </button>
                  </td>
                </tr>

                {expandedId === t._id && (
                  <tr>
                    <td colSpan="8" className="p-0">
                      <div className="bg-gray-50 border-b border-gray-200 p-6 animate-in fade-in slide-in-from-top-2 duration-200">
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                          
                          {/* 1. Reference */}
                          <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
                            <div className="flex items-center gap-2 mb-2">
                                <FiTag className="text-gray-400 text-sm" />
                                <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">Reference</span>
                            </div>
                            <p className="font-mono text-sm text-gray-800 font-semibold">{t.reference || "N/A"}</p>
                          </div>

                          {/* 2. Period / Date */}
                          <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
                            <div className="flex items-center gap-2 mb-2">
                                <FiCalendar className="text-gray-400 text-sm" />
                                <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">Period / Date</span>
                            </div>
                            <p className="text-sm text-gray-800 font-medium">
                                {t.category === 'Payroll' ? formatPeriod(t.details?.period) : formatDate(t.date)}
                            </p>
                          </div>

                          {/* 3. Specific Details */}
                          <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
                            <div className="flex items-center gap-2 mb-2">
                                <FiFileText className="text-gray-400 text-sm" />
                                <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">Details</span>
                            </div>
                            
                            {/* ITEMS LOGIC */}
                            {t.category === 'Item Consumption' ? (
                                <div className="text-sm">
                                    <p className="text-gray-800 font-medium">Qty: {t.details?.quantity || 0}</p>
                                    <p className="text-gray-500 text-xs">Price: ${Number(t.details?.unitPrice || 0).toFixed(2)}</p>
                                </div>
                            ) : t.category === 'Payroll' ? (
                                <p className="text-sm text-gray-800 font-medium">
                                    Gross: ${Number(t.details?.grossPay || 0).toLocaleString()}
                                </p>
                            ) : (
                                <p className="text-sm text-gray-800 capitalize">
                                    {t.details?.paymentMethod || t.details?.type || "Standard"}
                                </p>
                            )}
                          </div>

                          {/* 4. Extra Info */}
                          <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
                            <div className="flex items-center gap-2 mb-2">
                                <FiTag className="text-gray-400 text-sm" />
                                <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">Metadata</span>
                            </div>
                            <p className="text-sm text-gray-800 capitalize">
                                {t.details?.priority || t.details?.reason || "Standard"}
                            </p>
                          </div>

                        </div>

                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}